import { TRPCClientError } from "@trpc/client"

const MAX_QUERY_RETRIES = 3
const RETRY_BASE_DELAY_MS = 1000
const RETRY_MAX_DELAY_MS = 10_000
const RETRYABLE_CLIENT_STATUSES: ReadonlySet<number> = new Set([408, 429])
const FREIGHT_DASHBOARD_ROUTER = "freightDashboard"

export const FREIGHT_DASHBOARD_QUERY_KEY = [[FREIGHT_DASHBOARD_ROUTER]]
const FREIGHT_DASHBOARD_MAX_RETRIES = 1

const SLOW_QUERY_PATHS: ReadonlySet<string> = new Set([
    "freightDashboard.warehouseFlow",
])

const SHELL_PAINT_QUERY_PATHS: ReadonlySet<string> = new Set([
    "user.getMyUser",
    "org.getMyOrg",
    "org.getMyOrgs",
    "setup.isAppShown",
])

export function trpcPathOf(queryKey: readonly unknown[]): string | null {
    const [path] = queryKey
    if (!Array.isArray(path)) return null
    if (!path.every(segment => typeof segment === "string")) return null
    return path.join(".")
}

export const MAX_QUERIES_PER_BATCH = 10

export type QueryLane = "slow" | "shellPaint" | "shared"

export function queryLaneOf(path: string): QueryLane {
    if (SLOW_QUERY_PATHS.has(path)) return "slow"
    if (SHELL_PAINT_QUERY_PATHS.has(path)) return "shellPaint"
    return "shared"
}

export function isFreightDashboardQueryKey(
    queryKey: readonly unknown[]
): boolean {
    const path = trpcPathOf(queryKey)
    return path?.split(".")[0] === FREIGHT_DASHBOARD_ROUTER
}

function httpStatusOf(error: unknown): number | null {
    if (!(error instanceof TRPCClientError)) return null
    const status: unknown = error.data?.httpStatus
    return typeof status === "number" ? status : null
}

export function shouldRetryQuery(
    failureCount: number,
    error: unknown
): boolean {
    if (failureCount >= MAX_QUERY_RETRIES) return false
    const status = httpStatusOf(error)
    if (status === null) return true
    if (status >= 500) return true
    return RETRYABLE_CLIENT_STATUSES.has(status)
}

export function shouldRetryDashboardQuery(
    failureCount: number,
    error: unknown
): boolean {
    if (failureCount >= FREIGHT_DASHBOARD_MAX_RETRIES) return false
    return shouldRetryQuery(failureCount, error)
}

export function shouldRefetchDashboardOnReconnect(query: {
    state: { status: string }
}): boolean {
    return query.state.status === "error"
}

export function queryRetryDelay(attempt: number): number {
    return Math.min(RETRY_BASE_DELAY_MS * 2 ** attempt, RETRY_MAX_DELAY_MS)
}
